/**
 * Deriving what the caller actually heard from an assistant turn.
 *
 * On an interrupted turn the transcript's `text` is what the model generated,
 * which is routinely longer than what was spoken: the caller barged in, the audio
 * was flushed, and the tail was never voiced. Rendering `text` on its own shows a
 * finished sentence that nobody said out loud.
 *
 * These helpers are pure and separately testable so the rule that decides what a
 * reader is shown does not live inside a component.
 */

import type { SpeechMeasure, SpeechSplit } from "../types";

/**
 * Anything that might carry a measured cut.
 *
 * Structural rather than `TranscriptMessage` on purpose: the wall tile's preview
 * lines are `RecentMessage`, which carries the same compact fields, and both
 * surfaces answer the same question from them.
 */
export type CutCandidate = SpeechSplit & {
  role?: string;
  /** Not read here, but present so a full message can be passed straight in. */
  text?: string;
  item_id?: string;
  interrupted?: boolean;
};

export type CutTone = "severe" | "partial" | "total";

export interface CutSummary {
  /** True when there is a measured cut to show at all. */
  visible: boolean;
  /** Spoken share of the turn, 0..1. */
  ratio: number;
  /** The turn as the caller heard it. Empty when nothing was voiced. */
  spoken: string;
  /** The tail that was generated but never voiced. */
  unspoken: string;
  tone: CutTone;
  measure: SpeechMeasure;
  /** Short label, e.g. "34% spoken". */
  label: string;
  /** `true` when nothing at all was voiced before the cut. */
  silent: boolean;
  /** Text for the title attribute explaining how exact the number is. */
  basis: string;
}

const NO_CUT: CutSummary = {
  visible: false,
  ratio: 1,
  spoken: "",
  unspoken: "",
  tone: "partial",
  measure: "word",
  label: "",
  silent: false,
  basis: "",
};

/**
 * Read a turn's cut, if it has one.
 *
 * Returns `visible: false` for anything not worth rendering a cut marker for: an
 * ordinary turn, a caller's own turn (a caller who was interrupted is a
 * different problem and has no planned text to compare against), and a cut the
 * agent wrote before this feature existed, which carries only `interrupted`.
 *
 * That last case matters most for a gradual rollout: until every worker is
 * deployed, a cut turn arrives with no `spoken_ratio`, and inventing a number
 * from the elapsed time would be a guess dressed as a measurement.
 */
export function readCut(message: CutCandidate | null | undefined): CutSummary {
  if (!message) return NO_CUT;
  if (message.role !== "assistant") return NO_CUT;
  if (!message.interrupted) return NO_CUT;
  if (typeof message.spoken_ratio !== "number") return NO_CUT;

  const ratio = clamp01(message.spoken_ratio);
  const measure: SpeechMeasure = message.spoken_measure === "rate" ? "rate" : "word";
  const silent = ratio <= 0.001;

  return {
    visible: true,
    ratio,
    spoken: message.spoken_text ?? "",
    unspoken: message.unspoken_text ?? "",
    tone: toneFor(ratio),
    measure,
    label: silent ? "Nothing spoken" : `${Math.round(ratio * 100)}% spoken`,
    silent,
    basis: basisFor(measure, message.backlog_ms),
  };
}

export function toneFor(ratio: number): CutTone {
  if (ratio <= 0.001) return "total";
  // A turn the caller heard most of is not really an interruption to worry about.
  if (ratio >= 0.75) return "partial";
  return "severe";
}

export function basisFor(measure: SpeechMeasure, backlogMs?: number): string {
  const measured = measure === "word";
  const head = measured
    ? "Measured from TTS word timestamps: the last word the speaker voiced."
    : "Estimated from the speech-rate model — this provider emits no word timestamps.";
  if (!backlogMs || backlogMs <= 0) return `${head} Exact.`;
  return `${head} Within ±${Math.round(backlogMs)}ms, since that much audio was queued but unplayed.`;
}

/** The bar colour for a cut, matching the wall's existing tone vocabulary. */
export function barToneClass(tone: CutTone): string {
  if (tone === "total") return "bg-danger";
  if (tone === "severe") return "bg-warning";
  return "bg-info";
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Call-level totals for the drawer header, so a supervisor can see at a glance
 * how much of this call's agent speech actually reached the caller.
 */
export interface CallSpeechTotals {
  cutTurns: number;
  /** Mean spoken share across the cut turns. Null when nothing was cut. */
  meanRatio: number | null;
  /** Spoken characters across every cut turn. */
  spokenChars: number;
  /** Generated characters that never got voiced. */
  lostChars: number;
  visible: boolean;
}

export function callSpeechTotals(messages: readonly CutCandidate[] | null | undefined): CallSpeechTotals {
  const totals: CallSpeechTotals = {
    cutTurns: 0,
    meanRatio: null,
    spokenChars: 0,
    lostChars: 0,
    visible: false,
  };
  if (!messages?.length) return totals;

  let ratioSum = 0;
  for (const message of messages) {
    const cut = readCut(message);
    if (!cut.visible) continue;
    totals.cutTurns += 1;
    ratioSum += cut.ratio;
    totals.spokenChars += message.spoken_chars ?? countChars(cut.spoken);
    totals.lostChars += message.unspoken_chars ?? countChars(cut.unspoken);
  }
  if (!totals.cutTurns) return totals;

  totals.meanRatio = ratioSum / totals.cutTurns;
  totals.visible = true;
  return totals;
}

/** Character count for a fallback when the worker reported no char counts. */
function countChars(text: string): number {
  return text.trim().length;
}
